import React from 'react';
import { Search, Users, Megaphone, ArrowRight, TrendingUp, Target, Eye, Calculator } from 'lucide-react';
import { useCalculator } from '../context/CalculatorContext';
import { motion } from 'framer-motion';
import { AdSlot } from './AdSlot';
import { useAuth } from '../context/AuthContext';

const modules = [
  {
    id: 'google',
    title: 'Google Ads',
    description: 'CPC · Búsqueda y Display',
    longDescription: 'Calcula inversión basada en Costo por Clic para campañas de búsqueda y display.',
    icon: Search,
    gradient: 'from-blue-500 via-red-500 to-yellow-500',
    bgGradient: 'from-blue-50 to-white',
    iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
    metric: 'CPC',
    features: ['Búsqueda', 'Display', 'Shopping'],
  },
  {
    id: 'meta_leads',
    title: 'Meta y TikTok Ads',
    description: 'CPR / CPM · Prospectos y Tráfico',
    longDescription: 'Calcula inversión para campañas de prospectos (leads/ventas) o tráfico e interacción.',
    icon: Target,
    gradient: 'from-[#0668E1] to-[#FE2C55]',
    bgGradient: 'from-indigo-50 to-white',
    iconBg: 'bg-gradient-to-br from-[#0668E1] to-[#0080FB]',
    metric: 'CPR/CPM',
    features: ['Prospectos', 'Tráfico', 'Meta & TikTok'],
  },
  {
    id: 'meta_traffic',
    title: 'Presupuesto Publicitario Mensual',
    description: 'Calculadora completa de inversión digital',
    longDescription: 'Calcula tu presupuesto mensual, leads y clics necesarios, CPC máximo, ROAS e intensidad de marketing.',
    icon: Calculator,
    gradient: 'from-emerald-500 to-teal-500',
    bgGradient: 'from-emerald-50 to-white',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    metric: 'ROI',
    features: ['Presupuesto', 'ROAS', 'Viabilidad'],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export function HomeScreen() {
  const { setModule } = useCalculator();
  const { isAdmin } = useAuth();

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-6">
            <TrendingUp className="h-4 w-4" />
            Presupuestos inteligentes para tu inversión digital
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight mb-4">
            Calcula tu inversión
            <br />
            <span className="bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] bg-clip-text text-transparent">
              publicitaria
            </span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Selecciona la plataforma y tipo de campaña para obtener una estimación precisa de inversión basada en benchmarks de la industria.
          </p>
        </motion.div>

        {/* Module Cards */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
        >
          {modules.map((module) => (
            <motion.div
              key={module.id}
              variants={cardVariants}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setModule(module.id)}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${module.bgGradient} border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group`}
              data-testid={`module-card-${module.id}`}
            >
              {/* Gradient accent bar */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${module.gradient}`} />
              
              <div className="p-8">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${module.iconBg} shadow-lg mb-6`}>
                  <module.icon className="h-7 w-7 text-white" />
                </div>
                
                {/* Content */}
                <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-[#1A56DB] transition-colors">
                  {module.title}
                </h2>
                <p className="text-slate-500 text-sm mb-4">
                  {module.description}
                </p>
                <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                  {module.longDescription}
                </p>
                
                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {module.features.map((feature) => (
                    <span 
                      key={feature}
                      className="px-3 py-1 rounded-full bg-white/80 text-slate-600 text-xs font-medium border border-slate-200"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                
                {/* CTA */}
                <div className="flex items-center gap-2 text-[#1A56DB] font-semibold text-sm group-hover:gap-3 transition-all">
                  <span>Calcular {module.metric}</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* AdSense Slot 1 - Responsive Display */}
        {!isAdmin && (
          <AdSlot slotId="1111111111" variant="responsive" testId="home-ad-responsive" className="max-w-3xl mx-auto mt-10" />
        )}

        {/* Footer note */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-slate-500 text-sm mt-12 max-w-xl mx-auto"
        >
          Los resultados son estimaciones basadas en benchmarks de la industria. Los resultados reales dependen de la calidad creativa, la segmentación y el período de optimización del algoritmo.
        </motion.p>
      </div>
    </div>
  );
}
